import to from 'await-catch'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useMobileBack } from '../../../context/MobileBackContext'
import { addFlashMessage } from '../../../actions'
import { hideLoader, showLoader } from '../../../actions/loader'
import { getClinicsDetailed, getProfessionals, getSpecialties } from '../../../api'
import { mapToOptions } from '../../../utils/mapToOptions'
import ConfirmAppointment from './confirmAppointment/ConfirmAppointment'
import InitialForm from './InitialForm/InitialForm'
import SelectAppointment from './selectAppointment/SelectAppointment'
import SearchMap from './searchMap/searchMap'

const inFlightSpecialtiesByKey = {}

const Appointments = ({ patient, showLoader, hideLoader, addFlashMessage }, ...props) => {
	const [activeStep, setActiveStep] = useState(0)
	const { setCustomBack } = useMobileBack()
	const [formData, setFormData] = useState({
		speciality: '',
		clinic: '',
		clinicCode: '',
		professional: '',
		dateFrom: new Date(),
		dateTo: new Date(new Date().setMonth(new Date().getMonth() + 1)),
		hourFrom: null,
		hourTo: null
	})
	const [clinics, setClinics] = useState([])
	const [specialties, setSpecialties] = useState([])
	const [professionals, setProfessionals] = useState([])
	const [turnosDisponibles, setTurnosDisponibles] = useState([])
	const [selectedTurn, setSelectedTurn] = useState([])
	const patientObraSocialId = patient?.idObraSocial || patient?.obraSocial?.id
	const patientPlanId = patient?.idPlan || patient?.plan?.id

	const handleNext = values => {
		setFormData(prev => ({ ...prev, ...values }))
		setActiveStep(prev => prev + 1)
	}

	const handleUpdateFormData = values => {
		setFormData(prev => ({ ...prev, ...values }))
	}

	const handleBack = () => {
		setActiveStep(prev => prev - 1)
	}

	useEffect(() => {
		if (activeStep > 0) {
			setCustomBack(() => handleBack)
		} else {
			setCustomBack(null)
		}
		return () => setCustomBack(null)
	}, [activeStep])

	const onConfirm = turn => {
		setSelectedTurn(turn)
		setActiveStep(prev => prev + 1)
	}

	const renderStep = () => {
		switch (activeStep) {
			case 0:
				return (
					<InitialForm
						onNext={handleNext}
						onUpdateFormData={handleUpdateFormData}
						formData={formData}
						clinics={clinics}
						specialties={specialties}
						professionals={professionals}
						getProfessionals={fetchProfessionals}
					/>
				)
			case 1:
				return (
					<SearchMap
						formData={formData}
						onNext={handleNext}
						onUpdateFormData={handleUpdateFormData}
						clinics={clinics}
						specialties={specialties}
						professionals={professionals}
						onBack={handleBack}
						saveAppointments={setTurnosDisponibles}
						showLoader={showLoader}
						hideLoader={hideLoader}
						getProfessionals={fetchProfessionals}
					/>
				)
			case 2:
				return (
					<SelectAppointment
						onNext={handleNext}
						onBack={handleBack}
						turnosDisponibles={turnosDisponibles}
						onConfirm={onConfirm}
					/>
				)
			case 3:
				return <ConfirmAppointment onBack={handleBack} turn={selectedTurn} />
			default:
				return null
		}
	}

	useEffect(() => {
		const fetchSpecialties = async () => {
			try {
				showLoader()

				if (!patientObraSocialId || !patientPlanId) {
					setSpecialties([])
					return
				}

				const request = {
					idObraSocial: patientObraSocialId,
					idPlan: patientPlanId
				}
				const requestKey = `${patientObraSocialId}-${patientPlanId}`

				if (!inFlightSpecialtiesByKey[requestKey]) {
					inFlightSpecialtiesByKey[requestKey] = to(getSpecialties(request)).finally(() => {
						delete inFlightSpecialtiesByKey[requestKey]
					})
				}

				const [err, specialtiesResponse] = await inFlightSpecialtiesByKey[requestKey]

				if (err) throw new Error('Error cargando especialidades')

				const specialtiesData = specialtiesResponse.data || specialtiesResponse

				const optionsSpecialty = mapToOptions(specialtiesData, 'idEspecialidad', 'nombre')
				setSpecialties(optionsSpecialty)
			} catch (error) {
				addFlashMessage('Ocurrió un error cargando las especialidades')
			} finally {
				hideLoader()
			}
		}

		fetchSpecialties()
	}, [patientObraSocialId, patientPlanId])

	const fetchClinics = async speciality => {
		try {
			if (!patientObraSocialId || !patientPlanId) {
				setClinics([])
				return
			}

			const request = {
				IdEspecialidad: speciality,
				IdObraSocial: patientObraSocialId,
				IdPlan: patientPlanId
			}

			const [err, clinicsResponse] = await to(getClinicsDetailed(request))

			if (err) {
				setClinics([])
				return
			}

			const clinicsData = clinicsResponse.data || clinicsResponse
			const optionsClinic = mapToOptions(clinicsData, 'id', 'nombre', ['codigoEmpresa'])

			setClinics(optionsClinic)
		} catch (error) {
			addFlashMessage('Ocurrió un error cargando las clínicas')
			setClinics([])
		}
	}

	const fetchProfessionals = async (speciality, clinicCode) => {
		try {
			if (!patientObraSocialId || !patientPlanId) {
				setProfessionals([])
				return
			}

			const request = {
				idEspecialidad: speciality,
				idObraSocial: patientObraSocialId,
				idPlan: patientPlanId,
				esVideoconsulta: false,
				...(clinicCode ? { clinica: clinicCode } : {})
			}

			const [err, professionalsResponse] = await to(getProfessionals(request))

			if (err) {
				console.error('Error fetching professionals:', err)
				setProfessionals([])
				return
			}

			const optionsProfessional = mapToOptions(
				professionalsResponse.data || professionalsResponse,
				'idProfesional',
				'nombreProfesional'
			)
			setProfessionals(optionsProfessional)
		} catch (error) {
			addFlashMessage('Ocurrió un error cargando los datos')
			setProfessionals([])
		}
	}

	useEffect(() => {
		if (typeof formData.speciality !== 'number') {
			setProfessionals([])
			setClinics([])
		} else {
			fetchClinics(formData.speciality)
		}
	}, [formData.speciality])

	return <>{renderStep()}</>
}

const mapDispatchToProps = {
	showLoader,
	hideLoader,
	addFlashMessage
}

function mapStateToProps({ patient }) {
	return { patient }
}

export default connect(mapStateToProps, mapDispatchToProps)(Appointments)
